import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useNavigate, type LinkProps } from '@tanstack/react-router';
import { FileText, FileSignature, Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { MAX_SEARCH_LENGTH, MIN_SEARCH_LENGTH, useGlobalSearch } from '@/services/searchService';
import { cn } from '@/lib/utils';
import type { ContractSearchResult, DocumentSearchResult, SearchSnippetPart } from '@/types';

const DEBOUNCE_MS = 250;

type Target = Pick<LinkProps, 'to' | 'params' | 'hash'>;

const contractTarget = (contract: ContractSearchResult): Target => ({
  to: '/contracts/$id',
  params: { id: contract.id },
});

const documentTarget = (document: DocumentSearchResult): Target => ({
  to: '/contracts/$id',
  params: { id: document.contractId },
  hash: `document-${document.documentId}`,
});

/** Text nodes and <mark> only: the text comes from documents anyone can write. */
function Snippet({ parts }: { parts: SearchSnippetPart[] }) {
  if (parts.length === 0) return null;
  return (
    <p className="mt-0.5 text-xs text-grey-600 line-clamp-2" data-testid="search-snippet">
      {parts.map((part, index) =>
        part.match ? (
          <mark key={index} className="rounded-sm bg-amber-100 px-0.5 font-medium text-grey-800">
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </p>
  );
}

function GroupHeading({ id, children }: { id: string; children: string }) {
  return (
    <p id={id} role="presentation" className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-grey-500">
      {children}
    </p>
  );
}

/**
 * The search box in the app header (#27): contracts and document text, grouped, as a combobox. Arrow keys
 * move through the results, Enter opens the highlighted one (the first by default), Escape closes.
 */
export function GlobalSearch() {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const navigate = useNavigate();

  const trimmed = text.trim();
  useEffect(() => {
    const timer = setTimeout(() => setQuery(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);

  const { data, isFetching, isError } = useGlobalSearch(query, open);
  const searchable = trimmed.length >= MIN_SEARCH_LENGTH;
  const results = searchable ? data : undefined;
  const contracts = results?.contracts ?? [];
  const documents = results?.documents ?? [];
  const targets = [...contracts.map(contractTarget), ...documents.map(documentTarget)];
  const highlighted = Math.min(active, Math.max(targets.length - 1, 0));
  const showPanel = open && searchable;
  // The previous results stay on screen while the next load, but they cannot be chosen: Enter must not
  // open a result of text the person has already replaced.
  const current = results?.query === trimmed && !isFetching;
  const status = !showPanel
    ? ''
    : isError
      ? 'Search failed'
      : current
        ? `${targets.length} ${targets.length === 1 ? 'result' : 'results'}`
        : 'Searching';

  const optionId = (index: number) => `${listId}-${index}`;

  const close = () => {
    setOpen(false);
    setActive(0);
  };

  const finish = () => {
    close();
    setText('');
    setQuery('');
    inputRef.current?.blur();
  };

  const move = (step: number) => {
    const next = (highlighted + step + targets.length) % targets.length;
    setActive(next);
    // Focus stays in the input, so the list does not follow aria-activedescendant by itself.
    document.getElementById(optionId(next))?.scrollIntoView({ block: 'nearest' });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      close();
    } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && targets.length > 0) {
      event.preventDefault();
      if (!showPanel) {
        setOpen(true);
      } else {
        move(event.key === 'ArrowDown' ? 1 : -1);
      }
    } else if (event.key === 'Enter' && showPanel) {
      event.preventDefault();
      if (current && targets.length > 0) {
        void navigate(targets[highlighted]);
        finish();
      }
    }
  };

  const optionClass = (index: number) =>
    cn('block px-3 py-2 text-sm hover:bg-grey-50', index === highlighted && 'bg-grey-100');

  return (
    <div
      className="relative mx-2 min-w-0 flex-1 lg:mx-4 lg:w-96 lg:flex-none"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
    >
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-grey-400" />
      <Input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-label="Find contracts and documents"
        aria-expanded={showPanel}
        aria-controls={showPanel ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={showPanel && targets.length > 0 ? optionId(highlighted) : undefined}
        placeholder="Find contracts and documents"
        autoComplete="off"
        maxLength={MAX_SEARCH_LENGTH}
        className="h-9 pl-9"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        data-testid="global-search-input"
      />

      <p role="status" className="sr-only" data-testid="global-search-status">{status}</p>

      {showPanel && (
        <div
          id={listId}
          role="listbox"
          aria-label="Search results"
          aria-busy={!current}
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[70vh] overflow-y-auto rounded-lg border border-border bg-white pb-2 shadow-lg lg:left-auto lg:w-[32rem]"
          data-testid="global-search-results"
          onMouseDown={(event) => event.preventDefault()} // keeps focus, so blur cannot close the panel before the click (Safari does not focus links)
        >
          {isError ? (
            <p className="px-3 pt-3 text-sm text-red-600" role="alert">Search failed. Try again.</p>
          ) : !results ? (
            <p className="flex items-center gap-2 px-3 pt-3 text-sm text-grey-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching
            </p>
          ) : targets.length === 0 ? (
            isFetching || results.query !== trimmed ? (
              <p className="flex items-center gap-2 px-3 pt-3 text-sm text-grey-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching
              </p>
            ) : (
              <p className="px-3 pt-3 text-sm text-grey-500">No contracts or documents found.</p>
            )
          ) : (
            <>
              {contracts.length > 0 && (
                <div role="group" aria-labelledby={`${listId}-contracts`}>
                  <GroupHeading id={`${listId}-contracts`}>Contracts</GroupHeading>
                  {contracts.map((contract, index) => (
                    <Link
                      key={contract.id}
                      id={optionId(index)}
                      role="option"
                      tabIndex={-1}
                      aria-selected={index === highlighted}
                      {...contractTarget(contract)}
                      className={optionClass(index)}
                      onClick={finish}
                      onMouseEnter={() => setActive(index)}
                      data-testid="search-contract-result"
                    >
                      <span className="flex items-start gap-2">
                        <FileSignature className="mt-0.5 h-4 w-4 shrink-0 text-grey-400" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium text-grey-800">{contract.title}</span>
                          <span className="block truncate text-xs text-grey-500">
                            {contract.contractNumber} · {contract.vendorName}
                            {contract.matchedInDocuments && ' · found in a document'}
                          </span>
                          <Snippet parts={contract.snippet} />
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              )}

              {documents.length > 0 && (
                <div role="group" aria-labelledby={`${listId}-documents`}>
                  <GroupHeading id={`${listId}-documents`}>Documents</GroupHeading>
                  {documents.map((document, position) => {
                    const index = contracts.length + position;
                    return (
                      <Link
                        key={document.documentId}
                        id={optionId(index)}
                        role="option"
                        tabIndex={-1}
                        aria-selected={index === highlighted}
                        {...documentTarget(document)}
                        className={optionClass(index)}
                        onClick={finish}
                        onMouseEnter={() => setActive(index)}
                        data-testid="search-document-result"
                      >
                        <span className="flex items-start gap-2">
                          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-grey-400" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-grey-800">{document.fileName}</span>
                            <span className="block truncate text-xs text-grey-500">
                              {document.location} · {document.contractNumber} · {document.contractTitle}
                            </span>
                            <Snippet parts={document.snippet} />
                          </span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
